<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <label v-if="label" class="form-label" :class="{ required }">{{ label }}</label>
    <Datepicker
      v-model="internalValue"
      text-input
      :auto-apply="true"
      :enable-time-picker="false"
      :formats="datePickerFormats"
      :locale="datepickerLocale"
      :placeholder="placeholder"
      :clearable="clearable"
      :min-date="minDate ?? undefined"
      :max-date="maxDate ?? undefined"
      :disabled="disabled" />
    <FormErrorList
      v-if="errors && (Array.isArray(errors) ? errors.length > 0 : true)"
      :errors="Array.isArray(errors) ? errors : [errors]" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { FormErrorList } from '@prospective/lithium';
import { getDateFnsLocale } from '@/locale/dateFnsLocale';
import { parseDate } from '@/func/dateUtils';
import { formatDate } from '@/filters/dateFormat';
import translator from '@/locale/translator';

// stores a plain date (no time component), so this deliberately does not reuse PJAS_DATE_FORMAT
// (which is a datetime format) — the wire value is always a bare `yyyy-MM-dd` string
const DATE_ONLY_FORMAT = 'yyyy-MM-dd';

defineOptions({ inheritAttrs: false });

interface Props {
  modelValue: string | null;
  label?: string;
  placeholder?: string;
  clearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  minDate?: string | Date | null;
  maxDate?: string | Date | null;
  errors?: string | string[] | null;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  placeholder: undefined,
  clearable: true,
  required: false,
  disabled: false,
  minDate: null,
  maxDate: null,
  errors: null,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
}>();

const datePickerFormats = computed(() => ({ input: translator.get('date.format') || DATE_ONLY_FORMAT }));
const datepickerLocale = computed(() => getDateFnsLocale());

const internalValue = computed<Date | null>({
  get: () => parseDate(props.modelValue),
  set: (value) => emit('update:modelValue', value ? formatDate(value, DATE_ONLY_FORMAT) : null),
});
</script>

<style scoped>
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.form-label.required::after {
  content: '*';
  color: #ef4444;
  margin-left: 2px;
}
</style>
